import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { handleOAuthCallback } from '../api/auth';
import { useAuthStore } from '../store/authStore';
import { getOAuthState, clearOAuthState } from '../services/tokenStorage';

export default function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setTokens, setUser, setError } = useAuthStore();
  const [callbackError, setCallbackError] = useState<string | null>(null);

  // Prevent double-execution in React Strict Mode
  const processedRef = useRef(false);

  useEffect(() => {
    // Skip if already processed (React Strict Mode double-mount protection)
    if (processedRef.current) {
      return;
    }
    processedRef.current = true;

    const processCallback = async () => {
      try {
        // Extract OAuth parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Get provider from URL path, query params, or default to 'google'
        // Path format: /oauth/{provider}/callback or /auth/callback?provider=google
        const pathMatch = location.pathname.match(/\/oauth\/([^/]+)\/callback/);
        const provider = searchParams.get('provider') || pathMatch?.[1] || 'google';

        // Check for OAuth errors (user denied, etc.)
        if (error) {
          const message = errorDescription || error;
          throw new Error(`OAuth error: ${message}`);
        }

        if (!code || !state) {
          throw new Error('Missing OAuth parameters (code or state)');
        }

        // Validate CSRF state
        const savedState = getOAuthState();
        if (!savedState) {
          throw new Error('No saved OAuth state found (possible CSRF attack)');
        }
        if (state !== savedState) {
          throw new Error('Invalid OAuth state (CSRF protection failed)');
        }

        // Clear saved state immediately after validation
        clearOAuthState();

        // Exchange code for tokens
        const response = await handleOAuthCallback(provider, code, state);

        // Save to auth store (Zustand persist automatically saves to localStorage)
        setTokens(response.accessToken, response.refreshToken, response.expiresIn);
        setUser(response.user);

        // Redirect to original location or home
        const returnUrl = (location.state as { from?: string })?.from || '/';
        navigate(returnUrl, { replace: true });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Authentication failed';
        console.error('OAuth callback error:', err);
        setCallbackError(message);
        setError(message);

        // Redirect to login after error (after a short delay)
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    processCallback();
  }, [searchParams, location, navigate, setTokens, setUser, setError]);

  if (callbackError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {callbackError}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Redirecting to login...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      gap={2}
    >
      <CircularProgress size={60} />
      <Typography variant="h6">Completing sign in...</Typography>
    </Box>
  );
}
